import React, { useEffect, useRef, useState } from "react";
import {
  HeartGlyph, ArrowGlyph, AppleGlyph, PlayGlyph,
  PinGlyph, CrossGlyph, StarGlyph,
} from "./glyphs";
import { ParticleField, LoveSticker, Magnetic } from "./Effects";

const PEOPLE = [
  { name: "Anjali", age: 25, city: "Kochi",     img: "https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg?auto=compress&cs=tinysrgb&w=600",  tags: ["Reads at parks", "Pottery", "Indie films"] },
  { name: "Arjun",  age: 27, city: "Bengaluru", img: "https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=600", tags: ["Surfs at dawn", "Cooks", "Vinyl"] },
  { name: "Maya",   age: 24, city: "Mumbai",    img: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=600", tags: ["Painter", "Long walks", "Ramen"] },
  { name: "Theo",   age: 28, city: "Chennai",   img: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=600", tags: ["Marathoner", "Cinema", "Trekking"] },
];

const APP_STORE_URL  = "https://apps.apple.com/us/app/hridhayam/id6444903823";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.malayali_datingapp&hl=en&gl=US";

function FloatTag({ children, style, delay = 0 }) {
  return (
    <div className="float-tag"
         style={{ ...style, opacity: 0,
                  animation: `fadeUp .8s ${delay}s forwards, floatY 6s ${delay + 1}s ease-in-out infinite` }}>
      {children}
    </div>
  );
}

function NameRing() {
  const names = ["ANJALI · KOCHI", "ARJUN · BENGALURU", "MAYA · MUMBAI", "THEO · CHENNAI", "IRIS · DELHI", "ZARA · HYDERABAD", "KAI · PUNE", "MIRA · GOA"];
  return (
    <div className="name-ring">
      {names.map((n, i) => {
        const a = (i / names.length) * 360;
        const transform = `rotate(${a}deg) translate(220px) rotate(90deg)`;
        return <span key={n} style={{ transform }}>{n}</span>;
      })}
    </div>
  );
}

function UnderlineSquiggle() {
  return (
    <svg className="underline-svg" viewBox="0 0 200 12" preserveAspectRatio="none">
      <path d="M2 8 Q 25 0, 50 6 T 100 6 T 150 6 T 198 6" />
    </svg>
  );
}

function CountStat({ target, suffix = "", label, decimals = 0, formatK = false }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const t0 = performance.now();
          const dur = 1800;
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(target * eased);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);
  const display = formatK
    ? (n >= 1000 ? (n / 1000).toFixed(0) + "k" : Math.round(n))
    : n.toFixed(decimals);
  return (
    <div className="stat" ref={ref}>
      <div className="num"><em>{display}</em>{suffix}</div>
      <div className="label">{label}</div>
    </div>
  );
}

export default function Hero() {
  const phoneRef = useRef(null);
  const [stack, setStack] = useState(PEOPLE);
  const [swiping, setSwiping] = useState(null);
  const [match, setMatch] = useState(false);
  const swipingRef = useRef(false);

  useEffect(() => {
    const onMove = (e) => {
      if (!phoneRef.current) return;
      const r = phoneRef.current.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = (e.clientX - cx) / window.innerWidth;
      const dy = (e.clientY - cy) / window.innerHeight;
      phoneRef.current.style.transform = `perspective(1200px) rotateY(${dx * 8}deg) rotateX(${-dy * 8}deg) translateZ(0)`;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!phoneRef.current) return;
      const y = window.scrollY;
      phoneRef.current.style.translate = `0 ${-y * 0.06}px`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const runSwipe = (dir) => {
    if (swipingRef.current) return;
    swipingRef.current = true;
    setSwiping(dir);
    const showMatch = dir === "right" || dir === "super";
    if (showMatch) {
      setTimeout(() => setMatch(true), 380);
      setTimeout(() => setMatch(false), 1900);
    }
    setTimeout(() => {
      setStack((s) => [...s.slice(1), s[0]]);
      setSwiping(null);
      swipingRef.current = false;
    }, 720);
  };

  useEffect(() => {
    const id = setInterval(() => runSwipe("right"), 5200);
    return () => clearInterval(id);
  }, []);

  const swipe = (dir) => runSwipe(dir);

  return (
    <section className="hero">
      <div>
        <div className="hero-eyebrow">
          <span className="dot" />
          <span>Now in beta · Made with care</span>
        </div>

        <h1 className="hero-headline" data-cursor="text">
          <span className="line"><span>Find your</span></span>
          <span className="line"><span>person.</span></span>
          <span className="line"><span>Not just a</span></span>
          <span className="line"><span><em>swipe<UnderlineSquiggle /></em>.</span></span>
        </h1>

        <p className="hero-sub">
          A calmer, kinder dating app. Real people, verified profiles, conversations
          that actually go somewhere — all without the doomscroll.
        </p>

        <div className="hero-cta">
          <Magnetic strength={0.25}>
            <a className="btn btn-primary" href={APP_STORE_URL} target="_blank" rel="noreferrer">
              <AppleGlyph />
              <span>App Store</span>
              <ArrowGlyph />
            </a>
          </Magnetic>
          <Magnetic strength={0.25}>
            <a className="btn btn-secondary" href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
              <PlayGlyph />
              <span>Play Store</span>
            </a>
          </Magnetic>
        </div>
      </div>

      <div className="hero-visual">
        <ParticleField />
        <NameRing />
        <LoveSticker />
        <FloatTag style={{ top: "8%", left: "-2%" }} delay={1.6}>
          <HeartGlyph /> 12k matches today
        </FloatTag>
        <FloatTag style={{ top: "38%", right: "-6%" }} delay={1.85}>
          <HeartGlyph /> 100% verified
        </FloatTag>
        <FloatTag style={{ bottom: "16%", left: "-6%" }} delay={2.05}>
          <HeartGlyph /> Reply &lt; 2h
        </FloatTag>

        <div className="phone" ref={phoneRef}>
          <div className="phone-notch" />
          <div className="phone-screen">
            <div className="phone-statusbar">
              <span>9:41</span>
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 10 }}>●●●</span>
                <span style={{ width: 18, height: 9, border: "1px solid currentColor", borderRadius: 2, position: "relative", display: "inline-block" }}>
                  <span style={{ position: "absolute", inset: 1, width: "70%", background: "currentColor" }} />
                </span>
              </span>
            </div>

            <div className="swipe-stack">
              {stack.slice(0, 3).map((p, i) => {
                const top = i === 0 && swiping;
                const t =
                  top === "left"  ? "translateX(-140%) rotate(-22deg)" :
                  top === "right" ? "translateX(140%)  rotate(22deg)"  :
                  top === "super" ? "translateY(-140%) rotate(0)"      :
                  `translateY(${i * 8}px) scale(${1 - i * 0.04})`;
                return (
                  <div key={p.name + i} className="swipe-card"
                       style={{ transform: t, zIndex: 10 - i, opacity: i > 2 ? 0 : 1 }}>
                    <div className="swipe-card-img"
                         style={{ backgroundImage: `url(${p.img})`, backgroundSize: "cover", backgroundPosition: "center" }}>
                      <div className="swipe-card-info">
                        <div className="swipe-card-name">
                          {p.name} <span className="age">{p.age}</span>
                        </div>
                        <div className="swipe-card-meta">
                          <PinGlyph /> {p.city}
                        </div>
                        <div className="swipe-card-tags">
                          {p.tags.map((tg) => <span className="tag" key={tg}>{tg}</span>)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={"match-flash" + (match ? " show" : "")}>
              <div className="word">it's a match <span className="heart">&#9829;</span></div>
            </div>

            <div className="swipe-actions">
              <button className="swipe-btn x" onClick={() => swipe("left")} aria-label="Pass">
                <CrossGlyph />
              </button>
              <button className="swipe-btn star" onClick={() => swipe("super")} aria-label="Super like">
                <StarGlyph />
              </button>
              <button className="swipe-btn heart" onClick={() => swipe("right")} aria-label="Like">
                <HeartGlyph size={22} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="hero-stats">
        <CountStat target={250000} label="Active members" formatK />
        <CountStat target={12000}  suffix="+" label="Matches per week" />
        <CountStat target={4.8}    suffix="★" label="Average rating" decimals={1} />
        <CountStat target={92}     suffix="%" label="2nd-date rate" />
      </div>
    </section>
  );
}
